// src/utils/rmsStop.ts
//
// 楽天RMSにログインできず取得が止まっている店舗の判定と知らせの文（RN-283）。
// ConfigGate（初期設定を経由させる）・RmsStopBanner（全ページの赤い警告）・SettingsPage（初期設定の注意書き）で同じものを使う。

// 止まっている店舗を取得の入口で飛ばしたときの記録の文（RN-284）。
// apps/legacy/src/services/rms/core/browser/rlogin-credential-error.js の RMS_AUTH_STOPPED_MESSAGE と同じ文にする
export const RMS_AUTH_STOPPED_MESSAGE = "RMS認証情報が無効のため取得を停止中です（初期設定で再登録が必要）";

export type RmsStopStatus = {
    stopped: boolean;
    stoppedBy: "login" | "order" | null;
    lastOkAt: string | null;
};

// 止まっているか（ConfigGate・RmsStopBanner・SettingsPage で同じ決まりを使う）
// 止まっている＝止まっている印（rmsAuthStatus=invalid）・ログイン認証エラーの文・止まっている店舗を飛ばした回の文のどれか。
// ただし rmsAuthStatus=valid（楽天へのログインに成功した／お客様が保存した）のときは、文が残っていても止まっていない。
//   運営の「解除」のあと、ログインはできたが一部のデータだけ失敗すると、止まっている記録の文が
//   全部取れるまで残るため（apps/legacy の updateShopRefsDailyErrorSummaryWithPriority）
export function isRmsStoppedFrom(rmsAuthStatus: unknown, dataFetchErrLastMsg: unknown): boolean {
    const status = String(rmsAuthStatus ?? "").trim().toLowerCase();
    if (status === "invalid") return true;
    if (status === "valid") return false;
    const msg = String(dataFetchErrLastMsg ?? "");
    return msg.includes("ログイン認証エラーが発生しました") || msg.includes(RMS_AUTH_STOPPED_MESSAGE);
}

// GET /api/company/datafetch-status の応答から、止まっているかを読む
export function parseRmsStopStatus(json: any): RmsStopStatus {
    return {
        stopped: isRmsStoppedFrom(json?.rmsAuthStatus, json?.dataFetchErrLastMsg),
        stoppedBy: json?.rmsAuthStoppedBy === "order" ? "order" : json?.rmsAuthStoppedBy === "login" ? "login" : null,
        lastOkAt: typeof json?.dataFetchLastOkAt === "string" ? json.dataFetchLastOkAt : null,
    };
}

// 最後にデータを取れた日時（○月○日 ○時）
export function formatRmsLastOk(iso: string | null | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    // RN-289: 見る人のパソコンの時計ではなく、日本時間で出す
    const parts = new Intl.DateTimeFormat("ja-JP", {
        timeZone: "Asia/Tokyo",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        hourCycle: "h23",
    }).formatToParts(d);
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
    return `${get("month")}月${get("day")}日 ${Number(get("hour"))}時`;
}

// RN-289: 最後にデータを取れてから、この時間を超えたら「取得が遅れている」とする。
// 有料はふだん 01:30・10:30〜12:40・23:30 のどれかで取れる。無料は 10:30 の1回だけのことがあり（docs/scheduled-jobs.md）、
// 毎日の取れた時刻が少しずつずれても誤って知らせないよう、24時間に6時間のゆとりを足す（丸1日以上取れていないときだけ知らせる）
export const RMS_FETCH_DELAY_HOURS = 30;

// 最後にデータを取れた日時から、取得が遅れているか（日時が無い・読めないときは遅れていない扱い＝何も出さない側に倒す）
export function isRmsFetchDelayed(iso: string | null | undefined, nowMs: number = Date.now()): boolean {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return false;
    return nowMs - t > RMS_FETCH_DELAY_HOURS * 60 * 60 * 1000;
}

// 知らせの文（止まった理由ごと。「有効期限が切れている可能性」は使わない）
// headline＝太字で目立たせる1文目（止まっていること・最後に取れた日時）／body＝どうすればよいか
// onSettingsPage＝初期設定の画面の中で出すとき（受注の欄を「下の」で指す）
export function buildRmsStopParts(
    status: RmsStopStatus | null,
    opts?: { onSettingsPage?: boolean }
): { headline: string; body: string } {
    const label = formatRmsLastOk(status?.lastOkAt);
    const isOrder = status?.stoppedBy === "order";
    const headline =
        (isOrder
            ? "楽天RMSの受注データのダウンロードにログインできないため、データの更新が止まっています"
            : "楽天RMSにログインできないため、データの更新が止まっています") +
        (label ? `（最後にデータを取れた日時：${label}）` : "") +
        "。";
    const body = isOrder
        ? `受注データダウンロード用のユーザ名・パスワードを変更された場合は、${opts?.onSettingsPage ? "下の" : "初期設定の"}「受注データ データダウンロード用のユーザ名、パスワード」の欄で新しい内容をご登録ください。`
        : "R-Loginのパスワードを変更された場合は、初期設定の「RMSログイン設定」で新しいパスワードをご登録ください。";
    return { headline, body };
}

// 止まっている間は、アクセスのたびに（ブラウザのタブを開いて最初の1回）初期設定を経由させる。
// 経由したかはタブの中だけで覚える（sessionStorage）。会社ごとに分ける
function visitedKey(companyId: string | null | undefined) {
    return `rmsStopNoticeVisited.v1:${companyId ?? ""}`;
}

export function hasVisitedRmsStopNotice(companyId: string | null | undefined): boolean {
    try {
        return sessionStorage.getItem(visitedKey(companyId)) === "1";
    } catch {
        return false;
    }
}

export function markVisitedRmsStopNotice(companyId: string | null | undefined) {
    try {
        sessionStorage.setItem(visitedKey(companyId), "1");
    } catch {
        // 覚えられないときは毎回経由させる（安全側）
    }
}

// 止まっていないと分かったら忘れる（また止まったときに、もう一度経由させるため）
export function clearVisitedRmsStopNotice(companyId: string | null | undefined) {
    try {
        sessionStorage.removeItem(visitedKey(companyId));
    } catch {
        // 何もしない
    }
}

// ログアウトのときに、経由した記録と警告の控えを全部忘れる（ログインし直したら、もう一度経由させる）
export function clearAllRmsStopSession() {
    try {
        const keys: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const k = sessionStorage.key(i);
            if (k && (k.startsWith("rmsStopNoticeVisited.v1:") || k.startsWith("rmsStopStatus.v1:"))) keys.push(k);
        }
        keys.forEach((k) => sessionStorage.removeItem(k));
    } catch {
        // 何もしない
    }
}

// 全ページの警告の控え（ページを移るたびに警告が一度消えて出直す＝ちらつくのを防ぐ）
function statusKey(companyId: string | null | undefined) {
    return `rmsStopStatus.v1:${companyId ?? ""}`;
}

export function readCachedRmsStopStatus(companyId: string | null | undefined): RmsStopStatus | null {
    try {
        const raw = sessionStorage.getItem(statusKey(companyId));
        return raw ? (JSON.parse(raw) as RmsStopStatus) : null;
    } catch {
        return null;
    }
}

export function writeCachedRmsStopStatus(companyId: string | null | undefined, status: RmsStopStatus | null) {
    try {
        if (status) sessionStorage.setItem(statusKey(companyId), JSON.stringify(status));
        else sessionStorage.removeItem(statusKey(companyId));
    } catch {
        // 何もしない
    }
}
